import { useState } from 'react';
import { Menu, ArrowLeft, Building2 } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { useCalculatorStore } from '../../hooks/useCalculatorStore';
import { useChatStore } from '../../hooks/useChatStore';
import { useAuthStore } from '../../stores/authStore';
import { useAuditorStore } from '../../stores/auditorStore';
import { HeaderActions } from './HeaderActions';
import { useNavigate } from 'react-router-dom';
import { isAuditRole } from '../../utils/roles';

interface HeaderProps {
  onToggleSidebar: () => void;
}

export function Header({ onToggleSidebar }: HeaderProps) {
  const { user } = useAuth0();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const toggleCalculator = useCalculatorStore(state => state.toggle);
  const { displayMode, togglePanel } = useChatStore();
  const { user: authStoreUser } = useAuthStore();
  const { activeCompany } = useAuditorStore();
  const navigate = useNavigate();
  
  const isAuditor = isAuditRole(authStoreUser?.role);
  const isChatOpen = displayMode !== 'hidden';
  
  return (
    <header className="fixed top-0 left-0 right-0 bg-background border-b border-border z-30">
      <div className="h-16 px-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onToggleSidebar}
            className="text-muted-foreground hover:text-foreground focus:outline-none lg:hidden"
          >
            <Menu className="h-6 w-6" />
          </button>
          
          <div className="flex items-center gap-2">
            <span className="text-xl font-semibold text-primary hidden md:block">
              Wanzo Comptabilité
            </span>
            <span className="text-base font-semibold text-primary md:hidden">
              Wanzo
            </span>
          </div>

          {/* Auditor: Show active company + back to clients */}
          {isAuditor && activeCompany && (
            <div className="flex items-center gap-1.5 sm:gap-2 ml-1 sm:ml-2 pl-2 sm:pl-4 border-l border-border">
              <button
                onClick={() => navigate('/auditor/clients')}
                className="flex items-center gap-1 sm:gap-1.5 px-2 py-1 rounded-md text-primary bg-primary/10 hover:bg-primary/20 transition-colors shrink-0"
                title="Retour à la liste des entreprises"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                <span className="text-[11px] sm:text-xs font-medium hidden sm:inline">Mes clients</span>
              </button>
              <Building2 className="h-4 w-4 text-muted-foreground shrink-0 hidden sm:block" />
              <span className="text-xs sm:text-sm text-muted-foreground truncate max-w-[100px] sm:max-w-[200px]">
                {activeCompany.organization.name}
              </span>
            </div>
          )}
        </div>

        <HeaderActions
          onToggleCalculator={toggleCalculator}
          onToggleChat={togglePanel}
          isChatOpen={isChatOpen}
          hideChat={isAuditor && !activeCompany}
          onToggleUserMenu={() => setShowUserMenu(!showUserMenu)}
          user={user}
          showUserMenu={showUserMenu}
          onCloseUserMenu={() => setShowUserMenu(false)}
        />
      </div>
    </header>
  );
}